'use client'

import type { MouseEvent, ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { buildWhatsAppClickEvent } from '@/lib/lead-tracking'
import { createRefId, getStoredAttribution, pushDataLayer } from '@/lib/lead-tracking-client'
import { buildWhatsAppMessage, buildWhatsAppUrl, getWhatsAppContext } from '@/lib/whatsapp-link'

/** Click-to-chat link with a page-specific pre-filled message; pushes `whatsapp_click` to the dataLayer. */
export default function WhatsAppChatLink({
  className,
  children,
  ariaLabel,
  topic,
  servicio,
}: {
  className?: string
  children: ReactNode
  ariaLabel?: string
  /** Overrides the topic inferred from the URL (e.g. on Ads landings). */
  topic?: string
  servicio?: string
}) {
  const pathname = usePathname() || '/'
  const context = getWhatsAppContext(pathname)
  const resolvedTopic = topic ?? context.topic
  const resolvedServicio = servicio ?? context.servicio
  const messageContext = { locale: context.locale, topic: resolvedTopic }
  // Plain link (no ref) keeps working without JavaScript; the click handler adds the Ref code
  const href = buildWhatsAppUrl(buildWhatsAppMessage(messageContext))

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // auxclick also fires on right click (context menu): only middle click opens the chat
    if (event.type === 'auxclick' && event.button !== 1) return
    const { refId, origen } = createRefId(resolvedServicio)
    pushDataLayer(
      buildWhatsAppClickEvent({
        pagePath: pathname,
        servicio: resolvedServicio,
        refId,
        origen,
        utmCampaign: getStoredAttribution().utm_campaign,
      }),
    )
    // No preventDefault: the browser follows the updated href natively (works in in-app webviews and Ctrl+click)
    event.currentTarget.href = buildWhatsAppUrl(buildWhatsAppMessage(messageContext, refId))
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className={className}
      data-whatsapp-tracked="true"
      onClick={onClick}
      onAuxClick={onClick}
    >
      {children}
    </a>
  )
}
